import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { computed, effect, inject, Injectable, makeStateKey, PLATFORM_ID, REQUEST, signal, TransferState, untracked } from '@angular/core';
import { Router } from '@angular/router';

import { AuthStore } from '@rpgc-ai/store/auth/auth.store';
import { PersonaStore } from '@rpgc-ai/store/persona/persona.store';
import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

import type { IAuthSession, IUser } from '@rpgc-ai/shared-interfaces/interfaces/auth.interface';

import { authClient, authClientSteam, normalizeHeaders } from './better-auth.client';

import { LanguageService } from '../translate/language.service';

export interface SessionData {
  user: IUser;
  session: IAuthSession;
}

// Ключ для TransferState
const SESSION_DATA_KEY = makeStateKey<SessionData>('sessionData');

@Injectable({ providedIn: 'root' })
export class SessionService {
  // Injections
  readonly #authStore = inject(AuthStore);
  readonly #languageService = inject(LanguageService);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #profileStore = inject(ProfileStore);
  readonly #router = inject(Router);
  readonly #transferState = inject(TransferState);
  readonly #personaStore = inject(PersonaStore);

  readonly #request = inject(REQUEST, { optional: true });

  // Сигналы для состояния сессии
  readonly #sessionData = signal<SessionData | null>(null);
  readonly #isLoading = signal<boolean>(false);
  readonly #error = signal<string | null>(null);

  // Вычисляемые сигналы
  readonly user = computed<IUser | null>(() => this.#sessionData()?.user ?? null);
  readonly session = computed<IAuthSession | null>(() => this.#sessionData()?.session ?? null);

  readonly loadSession = signal<boolean>(false);
  readonly loadSessionAttempt = signal<number>(0);

  // In-flight запрос для дедупликации параллельных вызовов getSession
  #inflight: Promise<SessionData | null> | null = null;

  readonly #LOGOUT_EVENT_KEY = 'auth_logout_event';

  /**
   * Авторизован ли пользователь
   * Три состояния
   * undefined - пока не определено
   * true - авторизован
   * false - не авторизован
   */
  readonly isAuthenticated = signal<boolean | undefined>(undefined);

  constructor() {
    // Инициализируем данные из TransferState на клиенте перед запуском effects
    this.#initializeFromTransferState();

    // Автоматически загружаем сессию при инициализации
    this.#loadSessionEffect();
    this.#updateSessionEffect();
    this.#initializeStorageListener();
  }

  /**
   * Получает клиент для работы с аутентификацией
   */
  get client(): typeof authClient {
    return authClient;
  }

  /**
   * Получает клиент для работы с аутентификацией Steam
   */
  get clientSteam(): typeof authClientSteam {
    return authClientSteam;
  }

  /**
   * Получает текущую сессию пользователя
   * @param headers - Опциональные заголовки для SSR
   */
  async getSession(headers?: Record<string, string | string[] | undefined>): Promise<SessionData | null> {
    // Дедупликация: если запрос уже идёт — возвращаем тот же промис
    if (this.#inflight) {
      return this.#inflight;
    }

    this.#isLoading.set(true);
    this.#error.set(null);
    this.loadSessionAttempt.update((prev) => prev + 1);

    this.#inflight = (async () => {
      try {
        // Выполняем API запрос
        const requestHeaders = headers || (this.#request?.headers as unknown as Record<string, string>);
        const fetchOptions = requestHeaders ? { headers: normalizeHeaders(requestHeaders) } : undefined;

        const response = (await authClient.getSession({
          fetchOptions,
        })) as { data: SessionData | null };

        if (response.data) {
          this.#sessionData.set(response.data);
          this.isAuthenticated.set(true);

          // На сервере сохраняем в TransferState для передачи на клиент
          if (isPlatformServer(this.#platformId)) {
            this.#transferState.set(SESSION_DATA_KEY, response.data);
          }

          return response.data;
        } else {
          this.#sessionData.set(null);
          this.isAuthenticated.set(false);
          return null;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Ошибка при получении сессии';
        this.#error.set(errorMessage);
        this.#sessionData.set(null);
        this.isAuthenticated.set(false);
        return null;
      } finally {
        this.#isLoading.set(false);
        this.#inflight = null;
      }
    })();

    return this.#inflight;
  }

  /**
   * Обновляет сессию (refetch)
   */
  async refreshSession(): Promise<SessionData | null> {
    return this.getSession();
  }

  /**
   * Очищает данные сессии (например, при выходе)
   */
  clearSession(options: { notifyOtherTabs?: boolean; redirectToHome?: boolean } = {}): void {
    const { notifyOtherTabs = false, redirectToHome = false } = options;

    this.#sessionData.set(null);
    this.#error.set(null);
    this.#authStore.clearSession();
    this.#authStore.clearUser();
    this.#profileStore.clearProfile();
    this.#personaStore.clear();
    this.isAuthenticated.set(false);
    // Также очищаем TransferState если он еще есть
    if (this.#transferState.hasKey(SESSION_DATA_KEY)) {
      this.#transferState.remove(SESSION_DATA_KEY);
    }
    // Уведомляем другие вкладки если нужно
    if (notifyOtherTabs && isPlatformBrowser(this.#platformId)) {
      this.#notifyLogout();
    }

    if (redirectToHome && isPlatformBrowser(this.#platformId)) {
      const lang = this.#languageService.getCurrentLanguage();
      this.#router.navigate([lang]);
    }
  }

  /**
   * Проверяет, истекла ли сессия
   */
  isSessionExpired(): boolean {
    const session = this.#sessionData()?.session;
    if (!session) {
      return true;
    }

    return new Date() > new Date(session.expiresAt);
  }

  /**
   * Отзывает все остальные сессии пользователя
   */
  async revokeOtherSessions(): Promise<void> {
    try {
      await authClient.revokeOtherSessions();
      // После отзыва других сессий обновляем текущую
      await this.refreshSession();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при отзыве сессий';
      this.#error.set(errorMessage);
      throw error;
    }
  }

  /**
   * Инициализирует данные сессии из TransferState на клиенте
   */
  #initializeFromTransferState(): void {
    if (isPlatformBrowser(this.#platformId)) {
      const cachedSession = this.#transferState.get(SESSION_DATA_KEY, null);

      if (cachedSession) {
        this.#sessionData.set(cachedSession);
        this.isAuthenticated.set(true);

        // Удаляем из TransferState после использования
        this.#transferState.remove(SESSION_DATA_KEY);
      }
    }
  }

  /**
   * Инициализирует слушатель событий localStorage для синхронизации между вкладками
   */
  #initializeStorageListener(): void {
    if (isPlatformBrowser(this.#platformId)) {
      window.addEventListener('storage', (event) => {
        if (event.key === this.#LOGOUT_EVENT_KEY && event.newValue) {
          // Другая вкладка сделала logout - очищаем без уведомления
          this.clearSession({ redirectToHome: true });
        }
      });
    }
  }

  /**
   * Уведомляет другие вкладки о выходе из аккаунта
   */
  #notifyLogout(): void {
    localStorage.setItem(this.#LOGOUT_EVENT_KEY, Date.now().toString());
    // Очищаем через секунду
    setTimeout(() => {
      localStorage.removeItem(this.#LOGOUT_EVENT_KEY);
    }, 1000);
  }

  /**
   * Эффект обновления сессии
   */
  #updateSessionEffect(): void {
    effect(() => {
      const sessionData = this.#sessionData();
      if (!sessionData) {
        return;
      }

      const { user, session } = sessionData;

      untracked(() => {
        this.#authStore.setUser(user);
        this.#authStore.setSession(session);
      });
    });
  }

  /**
   * Effect для автоматической загрузки сессии
   */
  #loadSessionEffect(): void {
    effect(() => {
      const loadSession = this.loadSession();
      const hasUser = !!this.#authStore.user()?.id;
      const hasSessionData = !!this.#sessionData();
      const isLoading = this.#isLoading();
      const isAuthenticated = this.isAuthenticated();

      // Загружаем сессию если:
      // - запрошена загрузка
      // - нет данных сессии (в том числе из TransferState)
      // - нет активной загрузки
      // - нет пользователя в сторе
      // - статус аутентификации еще не определен (undefined) или не проверялся
      if (loadSession && !hasSessionData && !isLoading && !hasUser && isAuthenticated !== false) {
        untracked(() => {
          this.getSession();
        });
      }
    });
  }
}
